import {notFound, redirect} from "@tanstack/react-router";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {uuidZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";
import {PageType} from "@vesta/vos-types/modules/workspaceLevel/workspaceUiConfigs/domain/WorkspaceConfig";
import {CONFIG_PAGE_ROUTE_TO} from "~/workspaceConfig/configPageRoutes";
import {findConfigPageIdByType} from "~/workspaceConfig/configPageIds";
import {hasWorkspaceUiConfig} from "~/workspaceConfig/configRuntime";

function throwRedirectToConfigPageType(args: {
    workspaceId: Uuid;
    pageType: PageType;
    search?: Record<string, string | Uuid>;
}): void {
    if (hasWorkspaceUiConfig(args.workspaceId) !== true) {
        return;
    }
    const pageId = findConfigPageIdByType(args.workspaceId, args.pageType);
    if (pageId === undefined) {
        throw notFound();
    }
    throw redirect({
        to: CONFIG_PAGE_ROUTE_TO,
        params: {
            workspaceId: args.workspaceId,
            pageKey: pageId,
        },
        search: args.search ?? {},
        replace: true,
    });
}

/**
 * `/w/.../ottosync/data-sources/$dataSourceId` → config `PageType.DataSourceDetail`.
 * Config workspaces never load the production Ottosync page.
 */
export function throwRedirectToConfigDataSourceDetailWhenPresent(args: {
    workspaceId: Uuid;
    dataSourceId: string;
    connectionAttemptId?: Uuid;
}): void {
    const dataSourceIdResult = uuidZodValidator.safeParse(args.dataSourceId);
    if (dataSourceIdResult.success !== true) {
        if (hasWorkspaceUiConfig(args.workspaceId) === true) {
            throw notFound();
        }
        return;
    }
    throwRedirectToConfigPageType({
        workspaceId: args.workspaceId,
        pageType: PageType.DataSourceDetail,
        search: {
            dataSourceId: dataSourceIdResult.data,
            ...(args.connectionAttemptId === undefined
                ? {}
                : {connectionAttemptId: args.connectionAttemptId}),
        },
    });
}

export function throwRedirectToConfigDataConnectionsWhenPresent(workspaceId: Uuid): void {
    throwRedirectToConfigPageType({
        workspaceId: workspaceId,
        pageType: PageType.DataConnections,
    });
}

export function throwRedirectToConfigMyDataConnectionsWhenPresent(workspaceId: Uuid): void {
    throwRedirectToConfigPageType({
        workspaceId: workspaceId,
        pageType: PageType.MyDataConnections,
    });
}

export function throwRedirectToConfigConnectDataSourcesWhenPresent(args: {
    workspaceId: Uuid;
    onboardingJourneyId: Uuid;
    connectionAttemptId?: Uuid;
}): void {
    throwRedirectToConfigPageType({
        workspaceId: args.workspaceId,
        pageType: PageType.ConnectDataSources,
        search: {
            onboardingJourneyId: args.onboardingJourneyId,
            ...(args.connectionAttemptId === undefined
                ? {}
                : {connectionAttemptId: args.connectionAttemptId}),
        },
    });
}
